import type { AdminCatalogBatch, CatalogEventKind } from '@steado/admin-contract/catalog-history';

import { formatValue, text } from './history-model';

/**
 * How a pushed batch's unapplied entries read: what production kept and what
 * the batch wanted, or why the catalog refused it. The engine names fields in
 * camelCase and groups entries by domain; an operator reads labels.
 */

type Unapplied = AdminCatalogBatch['unapplied'][number];

const FIELD_LABELS: Readonly<
  Record<string, { label: string; format: 'kcal' | 'grams' | 'text' | 'bool' | 'enum' | 'list' }>
> = {
  kcalPer100g: { label: 'Energy per 100 g', format: 'kcal' },
  proteinGPer100g: { label: 'Protein per 100 g', format: 'grams' },
  carbsGPer100g: { label: 'Carbohydrate per 100 g', format: 'grams' },
  fatGPer100g: { label: 'Fat per 100 g', format: 'grams' },
  fibreGPer100g: { label: 'Fibre per 100 g', format: 'grams' },
  displayName: { label: 'Display name', format: 'text' },
  servableAsIs: { label: 'Eaten as it is', format: 'bool' },
  mealRole: { label: 'Meal role', format: 'enum' },
  healthTier: { label: 'Health tier', format: 'enum' },
  prepTimeBand: { label: 'Prep time', format: 'enum' },
  costBand: { label: 'Cost', format: 'enum' },
  seasonMonths: { label: 'Months in season', format: 'list' },
  mealTimes: { label: 'Meal times', format: 'list' },
  cuisines: { label: 'Cuisines', format: 'list' },
  isCommonInIndia: { label: 'Everyday food in India', format: 'bool' },
  dietClass: { label: 'Diet class', format: 'enum' },
};

const DOMAIN_LABELS: Readonly<Record<string, string>> = {
  nutrition: 'Energy and macros per 100 g',
  portions: 'Portions',
  'display-names': 'Display name and other names',
  servability: 'Eaten as it is',
  cuisines: 'Cuisines',
  common: 'Everyday food in India',
  'meal-roles': 'Meal role',
  feasibility: 'Prep time and cost',
  'health-tiers': 'Health tier',
  'serving-policies': 'Serving',
  'diet-classes': 'Diet class',
  'jain-exclusions': 'Jain',
  recipes: 'Recipe',
  seasonality: 'Months in season',
  'meal-times': 'Meal times',
  dishes: 'New dish',
  ingredients: 'New ingredient',
  exercises: 'Exercise',
  'exercise-attributes': 'Exercise details',
  'exercise-edges': 'Alternative',
  'exercise-animations': 'Demo clip',
  'meal-patterns': 'Meal pattern',
};

function portionsText(value: unknown): string {
  if (!Array.isArray(value)) return formatValue(value, 'text');
  if (value.length === 0) return 'none';
  return value
    .map(
      (portion: { label?: unknown; grams?: unknown }) => `1 ${text(portion.label)} ${text(portion.grams)} g`,
    )
    .join(', ');
}

function aliasesText(value: unknown): string {
  if (!Array.isArray(value)) return formatValue(value, 'text');
  return value.length === 0
    ? 'none'
    : value.map((alias: { alias?: unknown }) => text(alias.alias)).join(', ');
}

export function fieldLabel(field: string): string {
  if (field === 'portions') return 'Portions';
  if (field === 'aliases') return 'Other names';
  return FIELD_LABELS[field]?.label ?? field.replace(/([A-Z])/g, ' $1').toLowerCase();
}

export function fieldValue(field: string, value: unknown): string {
  if (field === 'portions') return portionsText(value);
  if (field === 'aliases') return aliasesText(value);
  return formatValue(value, FIELD_LABELS[field]?.format ?? 'text');
}

/** What the entry was about: its fields when the engine got that far, else its domain. */
export function unappliedLabel(entry: Unapplied): string {
  const fields = entry.fields ?? [];
  if (fields.length > 0) return fields.map((field) => fieldLabel(field.field)).join(', ');
  return DOMAIN_LABELS[entry.domain] ?? entry.domain;
}

/** Who and what made the change a conflict lost to: "from a console edit, Riya, today 09:12". */
export const KIND_PHRASES: Readonly<Record<CatalogEventKind, string>> = {
  console: 'a console edit',
  request: 'a food request',
  batch: 'a pushed batch',
  revert: 'a revert',
  system: 'a database update',
};

/** An engine refusal as a sentence: the slug prefix dropped, no em dashes. */
export function refusalText(reason: string, key: string): string {
  const text = reason.startsWith(`${key}: `) ? reason.slice(key.length + 2) : reason;
  const plain = text.replace(/\s+—\s+/g, ', ');
  return plain.length === 0
    ? plain
    : `${plain.charAt(0).toUpperCase()}${plain.slice(1)}${/[.!?]$/.test(plain) ? '' : '.'}`;
}
